import React from 'react';

interface SaudiLicensePlateProps {
  plateNumber: string;
}

const englishToArabic: { [key: string]: string } = {
  'A': 'ا', 'B': 'ب', 'J': 'ح', 'D': 'د', 'R': 'ر', 'S': 'س', 'X': 'ص',
  'T': 'ط', 'E': 'ع', 'G': 'ق', 'K': 'ك', 'L': 'ل', 'Z': 'م', 'N': 'ن',
  'H': 'ه', 'U': 'و', 'V': 'ى'
};

const toArabicDigit = (n: string): string => {
  const map: { [key: string]: string } = {
    '0': '٠', '1': '١', '2': '٢', '3': '٣', '4': '٤',
    '5': '٥', '6': '٦', '7': '٧', '8': '٨', '9': '٩'
  };
  return map[n] || n;
};

const parsePlateNumber = (plateNumber: string) => {
  const upper = plateNumber.toUpperCase();
  const letters = upper.replace(/[0-9]/g, '').slice(0, 3);
  const numbers = upper.replace(/[^0-9]/g, '').slice(0, 4);
  const arabicLettersArr = letters.split('').map(l => englishToArabic[l] || l);
  const arabicNumbersArr = numbers.split('').map(toArabicDigit);
  return {
    englishLettersArr: letters.split(''),
    englishNumbersArr: numbers.split(''),
    arabicLettersArr,
    arabicNumbersArr,
  };
};

// ── Shared cell style ────────────────────────────────────────────
const cellBase: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 'clamp(3px, 1vw, 8px)',
  padding: 'clamp(6px, 2vw, 14px) clamp(6px, 2vw, 12px)',
};

// ── Latin character span ─────────────────────────────────────────
const latinChar: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Arial Black', Arial, sans-serif",
  fontSize: 'clamp(15px, 5.5vw, 26px)',
  fontWeight: '900',
  lineHeight: 1,
  color: '#111',
  letterSpacing: 0,
};

// ── Arabic character span ────────────────────────────────────────
const arabicChar: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Noto Naskh Arabic', 'Amiri', 'Arial', sans-serif",
  fontSize: 'clamp(15px, 5.5vw, 26px)',
  fontWeight: '900',
  lineHeight: 1,
  color: '#111',
  letterSpacing: 0,
  // Shift Arabic glyphs down to visually match Latin baseline
  position: 'relative',
  top: '0.12em',
};

const SaudiLicensePlate: React.FC<SaudiLicensePlateProps> = ({ plateNumber }) => {
  const { englishLettersArr, englishNumbersArr, arabicLettersArr, arabicNumbersArr } =
    parsePlateNumber(plateNumber);

  return (
    <div style={{
      display: 'inline-block',
      width: '100%',
      maxWidth: '380px',
      margin: '0 auto',
    }}>
      <div style={{
        display: 'flex',
        backgroundColor: 'white',
        border: '3px solid #111',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
      }}>

        {/* ── Left 2×2 grid ── */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          borderRight: '2px solid #111',
        }}>

          {/* TOP-LEFT: Arabic numbers */}
          <div style={{ ...cellBase, borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
            {arabicNumbersArr.map((ch, i) => (
              <span key={i} style={arabicChar}>{ch}</span>
            ))}
          </div>

          {/* TOP-RIGHT: Arabic letters (reversed RTL) */}
          <div style={{ ...cellBase, borderBottom: '1px solid #ccc' }}>
            {arabicLettersArr.map((ch, i) => (
              <span key={i} style={arabicChar}>{ch}</span>
            ))}
          </div>

          {/* BOTTOM-LEFT: English numbers */}
          <div style={{ ...cellBase, borderRight: '1px solid #ccc' }}>
            {englishNumbersArr.map((ch, i) => (
              <span key={i} style={latinChar}>{ch}</span>
            ))}
          </div>

          {/* BOTTOM-RIGHT: English letters */}
          <div style={{ ...cellBase }}>
            {englishLettersArr.map((ch, i) => (
              <span key={i} style={latinChar}>{ch}</span>
            ))}
          </div>

        </div>

        {/* ── Right: KSA emblem ── */}
        <KsaEmblem />
      </div>
    </div>
  );
};

// ── KSA Emblem column ────────────────────────────────────────────
const KsaEmblem: React.FC = () => (
  <div style={{
    width: 'clamp(55px, 15vw, 80px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(4px, 1.5vw, 10px) clamp(4px, 1.5vw, 8px)',
    gap: '1px',
  }}>
    <img
      src="/ksa-emblem.svg"
      alt="KSA Emblem"
      style={{ width: 'clamp(32px, 9vw, 50px)', height: 'auto' }}
    />
    <span style={{
      fontSize: 'clamp(5px, 1.3vw, 7.5px)',
      fontFamily: "'Noto Naskh Arabic', Arial, sans-serif",
      color: '#1a1a1a',
      lineHeight: 1.2,
      textAlign: 'center',
      direction: 'rtl',
    }}>السعودية</span>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
      {['K', 'S', 'A'].map(l => (
        <span key={l} style={{
          fontFamily: "'Arial Black', Arial, sans-serif",
          fontSize: 'clamp(8px, 2.5vw, 14px)',
          fontWeight: '900',
          color: '#1a1a1a',
          lineHeight: 1.1,
        }}>{l}</span>
      ))}
    </div>
  </div>
);

export default SaudiLicensePlate;
